import { api } from './api'
import { WaCampaign, WaCampaignStatus, PaginatedResponse } from '@/types'

export interface WaCampaignFilters {
  accountId?: number
  status?: WaCampaignStatus
  search?: string
  page?: number
  limit?: number
}

export interface CreateWaCampaignRequest {
  accountId: number
  name: string
  templateId: number
  type?: 'IMMEDIATE' | 'SCHEDULED' | 'DRIP' | 'AB_TEST'
  scheduledAt?: string
  recipientIds?: number[]
}

export interface UpdateWaCampaignRequest {
  name?: string
  description?: string
  templateId?: number
  scheduledAt?: string
}

export interface AddRecipientsRequest {
  recipientIds: number[]
}

export interface RemoveRecipientsRequest {
  recipientIds: number[]
}

export interface WaCampaignStats {
  totalRecipients: number
  sentCount: number
  deliveredCount: number
  readCount: number
  failedCount: number
  pendingCount: number
  deliveryRate: number
  readRate: number
  recipientsByStatus?: Record<string, number>
}

export const waCampaignService = {
  async fetchCampaigns(filters: WaCampaignFilters = {}): Promise<PaginatedResponse<WaCampaign>> {
    const response = await api.get<{
      success: boolean
      data: WaCampaign[]
      pagination: {
        page: number
        limit: number
        total: number
        totalPages: number
      }
    }>('/whatsapp/campaigns', { params: filters })
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
    }
  },

  async getCampaign(id: number): Promise<WaCampaign> {
    const response = await api.get<{ success: boolean; data: WaCampaign }>(`/whatsapp/campaigns/${id}`)
    return response.data.data
  },

  async createCampaign(data: CreateWaCampaignRequest): Promise<WaCampaign> {
    const response = await api.post<{ success: boolean; data: WaCampaign }>('/whatsapp/campaigns', data)
    return response.data.data
  },

  async updateCampaign(id: number, data: UpdateWaCampaignRequest): Promise<WaCampaign> {
    const response = await api.put<{ success: boolean; data: WaCampaign }>(`/whatsapp/campaigns/${id}`, data)
    return response.data.data
  },

  async deleteCampaign(id: number): Promise<void> {
    await api.delete(`/whatsapp/campaigns/${id}`)
  },

  async startCampaign(id: number): Promise<WaCampaign> {
    const response = await api.post<{ success: boolean; data: WaCampaign }>(`/whatsapp/campaigns/${id}/start`)
    return response.data.data
  },

  async pauseCampaign(id: number): Promise<WaCampaign> {
    const response = await api.post<{ success: boolean; data: WaCampaign }>(`/whatsapp/campaigns/${id}/pause`)
    return response.data.data
  },

  async resumeCampaign(id: number): Promise<WaCampaign> {
    const response = await api.post<{ success: boolean; data: WaCampaign }>(`/whatsapp/campaigns/${id}/resume`)
    return response.data.data
  },

  async cancelCampaign(id: number): Promise<WaCampaign> {
    const response = await api.post<{ success: boolean; data: WaCampaign }>(`/whatsapp/campaigns/${id}/cancel`)
    return response.data.data
  },

  async addRecipients(id: number, data: AddRecipientsRequest): Promise<{ added: number }> {
    const response = await api.post<{ success: boolean; data: { added: number } }>(
      `/whatsapp/campaigns/${id}/recipients`,
      data
    )
    return response.data.data
  },

  async removeRecipients(id: number, data: RemoveRecipientsRequest): Promise<{ removed: number }> {
    const response = await api.delete<{ success: boolean; data: { removed: number } }>(
      `/whatsapp/campaigns/${id}/recipients`,
      { data }
    )
    return response.data.data
  },

  async getRecipients(
    id: number,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<{ contactId: number; status: string; contact?: any }>> {
    const response = await api.get<{
      success: boolean
      data: any[]
      pagination: any
    }>(`/whatsapp/campaigns/${id}/recipients`, { params })
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
    }
  },


  async retryCampaign(id: number): Promise<WaCampaign> {
    const response = await api.post<{ success: boolean; data: WaCampaign }>(`/whatsapp/campaigns/${id}/retry`)
    return response.data.data
  },
  async getStats(id: number): Promise<WaCampaignStats> {
    const response = await api.get<{ success: boolean; data: WaCampaignStats }>(
      `/whatsapp/campaigns/${id}/stats`
    )
    return response.data.data
  },
}
